import React, { memo, useEffect } from 'react';
import { Col, Form, Input, InputNumber, Row, Space, Typography } from 'antd';
import styled from 'styled-components';
import { COLORS } from '../../../utils/constants';
import { updateCourtRequest } from '../../../api/requests/sports';
import { CourtDto, UpdateCourtType } from '../../../types/types';
import Button from '../../common/Button';
import Modal from '../../common/Modal';
import { CourtStatus } from '../../../enums/enums';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { handleNumericKeyDown, parseNumericInput } from '../../../utils/utils';

interface CourtFormValues {
  courtName: string;
  weekdayPrice?: number;
  weekendPrice?: number;
}

interface CourtFormModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: () => void;
  initialValues: CourtDto;
}

const UpdateCourtModal: React.FC<CourtFormModalProps> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
}) => {
  const [form] = Form.useForm<CourtFormValues>();
  const snackbar = useSnackbar();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values) {
        const data: UpdateCourtType = {
          courtId: initialValues?.courtId,
          courtName: values.courtName,
          courtDescription: '',
          courtStatus: CourtStatus.Active,
          regularHourlyPrice: values.weekdayPrice,
          weekendHourlyPrice: values.weekendPrice,
        };
        try {
          const response = await updateCourtRequest(data);
          if (response?.isSuccess) {
            onSubmit();
            form.resetFields();
            snackbar.success('Court updated successfully');
          } else {
            snackbar.error(response?.error);
          }
        } catch (error: any) {
          snackbar.error(error?.Error || 'Error updating court');
        }
      }
    } catch (error) {
      console.warn('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        courtName: initialValues.courtName,
        weekdayPrice: initialValues.regularHourlyPrice,
        weekendPrice: initialValues.weekendHourlyPrice,
      });
    }
  }, [initialValues, form]);

  return (
    <Modal
      title={'Edit Court'}
      open={visible}
      onCancel={handleCancel}
      minHeight={'auto'}
    >
      <Form form={form} layout="vertical">
        <CourtsContainer>
          <CourtLabel>
            <MandatorySymbol>*</MandatorySymbol> Court Name
          </CourtLabel>

          <StyledCourtSpace align="center">
            <Col>
              <CourtFormItem
                name="courtName"
                rules={[{ required: true, message: 'Please enter court name' }]}
              >
                <StyledInput placeholder="Enter court name" size="large" />
              </CourtFormItem>
              <Row justify="space-between" gutter={16}>
                <Col span={12}>
                  <CourtLabel>Weekday(₹/hr)</CourtLabel>
                  <CourtFormItem name="weekdayPrice">
                    <StyledInputNumber
                      size="large"
                      placeholder="499"
                      min="0"
                      step="1"
                      parser={parseNumericInput}
                      onKeyDown={handleNumericKeyDown}
                    />
                  </CourtFormItem>
                </Col>
                <Col span={12}>
                  <CourtLabel>Weekend(₹/hr)</CourtLabel>
                  <CourtFormItem name="weekendPrice">
                    <StyledInputNumber
                      size="large"
                      placeholder="799"
                      min="0"
                      step="1"
                      parser={parseNumericInput}
                      onKeyDown={handleNumericKeyDown}
                    />
                  </CourtFormItem>
                </Col>
              </Row>
            </Col>
          </StyledCourtSpace>
        </CourtsContainer>

        <ButtonSection>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit}>
            Update Court
          </Button>
        </ButtonSection>
      </Form>
    </Modal>
  );
};

const StyledFormItem = styled(Form.Item)`
  width: 100%;
  &.ant-space-item {
    flex: 1;
  }
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 32px;
`;

const MandatorySymbol = styled.span`
  color: red;
`;

const StyledCourtSpace = styled(Space)`
  display: flex;
  width: 100%;
  & .ant-space-item:first-child {
    flex: 1;
  }
  margin-bottom: 10px;
`;

const CourtsContainer = styled.div`
  margin-bottom: 16px;
`;

const CourtLabel = styled(Typography.Text)`
  display: block;
  margin-bottom: 8px;
`;

const CourtFormItem = styled(StyledFormItem)`
  flex: 1;
  margin-bottom: 0;
`;

const StyledInput = styled(Input)`
  width: 100%;
  height: 40px;
  margin-bottom: 8px;

  .ant-input-group-addon {
    font-size: 12px;
    padding: 0 8px;
    background-color: ${COLORS.grey.primary};
    color: ${COLORS.grey.secondary};
  }
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
`;

export default memo(UpdateCourtModal);
